import { useState } from 'react';
import { api, getToken } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSort, SortTh } from '../lib/useSort.tsx';
import { titleCase } from '../lib/format.ts';
import { Card, Empty, ErrorBanner, Loading, Status } from '../components/ui.tsx';

interface Account {
  account_number: string;
  legacy_account_number: string | null;
  account_name: string;
  account_type: string;
  normal_balance: string;
  department_name: string | null;
  is_postable: boolean;
  is_active: boolean;
  control_account_for: string | null;
}

export function ChartOfAccountsPage() {
  const { data, error, loading } = useApi<{ accounts: Account[] }>('/v1/coa');
  const [filter, setFilter] = useState('');

  const rows = (data?.accounts ?? []).filter(
    (a) =>
      !filter ||
      a.account_number.includes(filter) ||
      a.account_name.toLowerCase().includes(filter.toLowerCase()) ||
      (a.legacy_account_number ?? '').includes(filter),
  );

  /* Sorted by account number to begin with, because that is the order a
     chart of accounts is written in and the order an auditor reads it. */
  const sorted = useSort(
    rows,
    {
      account: (a) => a.account_number,
      legacy: (a) => a.legacy_account_number,
      name: (a) => a.account_name,
      type: (a) => a.account_type,
      department: (a) => a.department_name,
    },
    { key: 'account' },
  );

  async function download(locale?: string) {
    // The export endpoint returns CSV rather than JSON, so this bypasses the
    // usual client and streams the blob straight to a download.
    const res = await fetch(`/v1/coa/export${locale ? `?locale=${locale}` : ''}`, {
      headers: { authorization: `Bearer ${getToken()}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = locale ? `chart-of-accounts-${locale}.csv` : 'chart-of-accounts.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <h1>Chart of Accounts</h1>
        <div className="spacer" />
        <button className="secondary" onClick={() => download()}>
          Export CSV
        </button>
        <a href="/coa/import">
          <button className="gold">Import</button>
        </a>
      </div>

      <ErrorBanner error={error} />

      <Card>
        <input
          placeholder="Filter by number or name"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ maxWidth: 340 }}
        />
        <div className="hint">
          The <strong>Your number</strong> column is the club's own account number, kept
          permanently as an alias. Reports and exports carry both.
        </div>
      </Card>

      <Card flush>
        {loading ? (
          <div style={{ padding: 16 }}>
            <Loading rows={8} />
          </div>
        ) : rows.length === 0 ? (
          <Empty>No accounts match.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <SortTh s={sorted} col="account">Account</SortTh>
                <SortTh s={sorted} col="legacy">Your number</SortTh>
                <SortTh s={sorted} col="name">Name</SortTh>
                <SortTh s={sorted} col="type">Type</SortTh>
                <SortTh s={sorted} col="department">Department</SortTh>
                <th />
              </tr>
            </thead>
            <tbody>
              {sorted.rows.map((a) => (
                <tr key={a.account_number}>
                  <td className="mono">{a.account_number}</td>
                  <td className="mono muted">{a.legacy_account_number ?? '—'}</td>
                  <td>{a.account_name}</td>
                  <td className="muted">{titleCase(a.account_type)}</td>
                  <td className="muted">{a.department_name ?? '—'}</td>
                  <td className="small">
                    {a.control_account_for && (
                      <Status tone="neutral">{a.control_account_for.toUpperCase()} control</Status>
                    )}
                    {!a.is_postable && <span className="muted"> roll-up</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Import wizard                                                       */
/* ------------------------------------------------------------------ */

interface ImportRow {
  row_number: number;
  external_code: string | null;
  account_name: string | null;
  account_type: string | null;
  action: 'pending' | 'create' | 'map' | 'skip' | 'error';
  messages: { level: string; text: string }[];
}

interface Validation {
  errors: number;
  warnings: number;
  ready: number;
  rows_needing_attention: ImportRow[];
}

/**
 * Three explicit steps, matching what the API enforces: stage, validate,
 * commit. A single "upload and hope" button would produce half-imported
 * charts that nobody can unpick, so the flow deliberately makes the
 * controller look at what will happen before anything moves.
 */
export function CoaImportPage() {
  const [csv, setCsv] = useState('');
  const [filename, setFilename] = useState('');
  const [source, setSource] = useState('csv');
  const [batchId, setBatchId] = useState<string | null>(null);
  const [validation, setValidation] = useState<Validation | null>(null);
  const [result, setResult] = useState<{ created: number; mapped: number; skipped: number } | null>(null);

  const stage = useMutation(async () =>
    api<{ id: string; row_count: number }>('/v1/coa/imports', {
      method: 'POST',
      body: { source_system: source, source_filename: filename || undefined, csv },
    }),
  );
  const validate = useMutation(async (id: string) =>
    api<Validation>(`/v1/coa/imports/${id}/validate`, { method: 'POST' }),
  );
  const commit = useMutation(async (id: string) =>
    api<{ created: number; mapped: number; skipped: number }>(`/v1/coa/imports/${id}/commit`, {
      method: 'POST',
    }),
  );

  async function onFile(file: File) {
    setFilename(file.name);
    setCsv(await file.text());
    setBatchId(null);
    setValidation(null);
    setResult(null);
  }

  async function doStage() {
    const r = await stage.run();
    if (!r) return;
    setBatchId(r.id);
    const v = await validate.run(r.id);
    if (v) setValidation(v);
  }

  async function doRevalidate() {
    if (!batchId) return;
    const v = await validate.run(batchId);
    if (v) setValidation(v);
  }

  async function doCommit() {
    if (!batchId) return;
    const r = await commit.run(batchId);
    if (r) setResult(r);
  }

  return (
    <>
      <h1 style={{ marginBottom: 4 }}>Import a chart of accounts</h1>
      <p className="muted small" style={{ marginTop: 0, marginBottom: 18 }}>
        Your existing chart is mapped, not replaced. An account number that already exists here
        is linked rather than duplicated, and your own numbers are kept permanently as aliases.
      </p>

      <ErrorBanner error={stage.error} />
      <ErrorBanner error={validate.error} />
      <ErrorBanner error={commit.error} />

      {result ? (
        <div className="ok-banner">
          <strong>Import complete.</strong> {result.created} account
          {result.created === 1 ? '' : 's'} created, {result.mapped} mapped to the existing chart,{' '}
          {result.skipped} skipped.
          <div style={{ marginTop: 8 }}>
            <a href="/coa">
              <button className="secondary small">View the chart</button>
            </a>
          </div>
        </div>
      ) : (
        <>
          <Card title="1 · Choose the File">
            <label className="field">
              <span>Where did it come from?</span>
              <select value={source} onChange={(e) => setSource(e.target.value)} style={{ maxWidth: 260 }}>
                <option value="csv">A spreadsheet or CSV</option>
                <option value="jonas">Jonas</option>
                <option value="clubessential">Clubessential</option>
                <option value="northstar">Northstar</option>
                <option value="clubsoft">ClubSoft</option>
                <option value="quickbooks">QuickBooks</option>
                <option value="sage">Sage</option>
                <option value="xero">Xero</option>
                <option value="other">Something else</option>
              </select>
              <div className="hint">
                Header names from these systems are recognized automatically — “GL Code”,
                “Account #”, “Description”, and so on.
              </div>
            </label>

            <label className="field">
              <span>CSV file</span>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void onFile(f);
                }}
              />
            </label>

            {csv && (
              <>
                <div className="muted small">
                  {filename} · {csv.split('\n').filter(Boolean).length - 1} data rows
                </div>
                <div className="actions" style={{ marginTop: 12 }}>
                  <button onClick={doStage} disabled={stage.busy || validate.busy}>
                    {stage.busy || validate.busy ? 'Checking…' : 'Check this file'}
                  </button>
                </div>
              </>
            )}
          </Card>

          {validation && (
            <>
              <Card title="2 · What Will Happen">
                <div className="tiles" style={{ marginBottom: 0 }}>
                  <div className="tile">
                    <div className="tile__label">Ready to import</div>
                    <div className="tile__value">{validation.ready}</div>
                  </div>
                  <div className="tile">
                    <div className="tile__label">Warnings</div>
                    <div className="tile__value">{validation.warnings}</div>
                    <div className="tile__note">
                      <Status tone={validation.warnings > 0 ? 'warning' : 'good'}>
                        {validation.warnings > 0 ? 'will still import' : 'none'}
                      </Status>
                    </div>
                  </div>
                  <div className="tile">
                    <div className="tile__label">Errors</div>
                    <div className="tile__value">{validation.errors}</div>
                    <div className="tile__note">
                      <Status tone={validation.errors > 0 ? 'critical' : 'good'}>
                        {validation.errors > 0 ? 'blocks the import' : 'none'}
                      </Status>
                    </div>
                  </div>
                </div>

                {validation.errors > 0 && (
                  <div className="notice" style={{ marginTop: 16, marginBottom: 0 }}>
                    Nothing will be imported while any row is in error. Fix the source file and
                    check it again — a partially imported chart of accounts is worse than none.
                  </div>
                )}
              </Card>

              {validation.rows_needing_attention.length > 0 && (
                <Card title="Rows to Look At" flush>
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Account</th>
                        <th>Name</th>
                        <th>Action</th>
                        <th>What we found</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validation.rows_needing_attention.map((r) => (
                        <tr key={r.row_number}>
                          <td className="muted">{r.row_number}</td>
                          <td className="mono">{r.external_code ?? '—'}</td>
                          <td>{r.account_name ?? <span className="muted">missing</span>}</td>
                          <td>
                            <Status
                              tone={
                                r.action === 'error'
                                  ? 'critical'
                                  : r.action === 'map'
                                    ? 'neutral'
                                    : 'good'
                              }
                            >
                              {r.action === 'map' ? 'Link to existing' : titleCase(r.action)}
                            </Status>
                          </td>
                          <td className="small">
                            {r.messages.map((m, i) => (
                              <div key={i} className={m.level === 'error' ? 'neg' : 'muted'}>
                                {m.text}
                              </div>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              )}

              <Card title="3 · Import">
                <div className="actions">
                  <button onClick={doCommit} disabled={validation.errors > 0 || commit.busy} className="gold">
                    {commit.busy ? 'Importing…' : `Import ${validation.ready} accounts`}
                  </button>
                  <button className="secondary" onClick={doRevalidate} disabled={validate.busy}>
                    Check again
                  </button>
                  {validation.errors > 0 && (
                    <span className="muted small">
                      Upload a corrected file above, then check again.
                    </span>
                  )}
                </div>
              </Card>
            </>
          )}
        </>
      )}
    </>
  );
}
